import { useCreateTreatments } from "API/Queries/useCreateTreatments";
import { useDeleteTreatments } from "API/Queries/useDeleteTreatments";
import { useEditTreatment } from "API/Queries/useEditTreatments";
import {
  Treatment,
  useGetAllTreatments,
} from "API/Queries/useGetAllTreatments";
import { useParams } from "react-router-dom";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import { getNextAvailableColor } from "Utils/utils";

export const useHandleTreatments = () => {
  const {
    existingTreatments,
    newTreatments,
    deletedTreatments,
    editedTreatments,
    duplicateErrors,
    setDuplicateErrors,
    setNewTreatments,
    setEditedTreatments,
    setDeletedTreatments,
  } = useFeatureFlipStore();

  const { id, featureId } = useParams();
  const { mutateAsync: createTreatmentsAsync } = useCreateTreatments();
  const { mutateAsync: deleteTreatmentsAsync } = useDeleteTreatments();
  const { mutateAsync: editTreatment } = useEditTreatment();

  const { refetch: getAllTreatments } = useGetAllTreatments(
    featureId as string
  );

  const validateTreatmentName = (
    name: string,
    index: number,
    isNew = false
  ) => {
    const allTreatments = [...existingTreatments, ...newTreatments];
    const duplicate = allTreatments.findIndex(
      (t, i) =>
        ((!isNew && i !== index) ||
          (isNew && i !== index + existingTreatments.length)) &&
        t.name.toLowerCase() === name.toLowerCase()
    );

    if (duplicate !== -1) {
      const errorMessage = "Treatment name must be unique";

      setDuplicateErrors({
        ...duplicateErrors,
        [isNew ? `new_${index}` : index]: errorMessage,
      });
      return false;
    }

    const newErrors = { ...duplicateErrors };
    delete newErrors[isNew ? `new_${index}` : index];
    setDuplicateErrors(newErrors);
    return true;
  };

  const getNextTreatmentNumber = () => {
    const allTreatments = [...existingTreatments, ...newTreatments];
    const existingNumbers = allTreatments
      .map((t) => {
        // Try to extract number from treatment name
        const match = t.name.match(/^treatment_(\d+)$/);
        if (match) {
          return parseInt(match[1]);
        }
        // If no match, consider it as a regular treatment
        return 0;
      })
      .filter((num) => !isNaN(num));

    // If no numbers found, start from the total count of treatments plus 1
    if (existingNumbers.length === 0) {
      return allTreatments.length + 1;
    }

    // Otherwise, get the max number and add 1
    return Math.max(...existingNumbers, allTreatments.length) + 1;
  };

  const handleTreatmentChange = (
    index: number,
    field: string,
    value:
      | string
      | Record<string, string>
      | null
      | {
          new: string[];
          deleted: string[];
          existing: string[];
        },
    isNew?: boolean
  ) => {
    if (isNew) {
      const updatedNewTreatments = [...newTreatments];
      updatedNewTreatments[index] = {
        ...updatedNewTreatments[index],
        [field]: value,
      };

      if (field === "name") {
        validateTreatmentName(value as string, index, true);
      }

      setNewTreatments(updatedNewTreatments);
    } else {
      const updatedTreatments = [...editedTreatments];
      const currentTreatment = updatedTreatments[index];

      // Create updated treatment with new value
      const updatedTreatment = {
        ...currentTreatment,
        [field]: value,
      };

      // Check if any tracked field has changed
      const hasChanged =
        currentTreatment.name !== updatedTreatment.name ||
        currentTreatment.description !== updatedTreatment.description ||
        JSON.stringify(currentTreatment.configuration) !==
          JSON.stringify(updatedTreatment.configuration);

      if (hasChanged) {
        const existingIndex = editedTreatments.findIndex(
          (t) => t.id === currentTreatment.id
        );
        if (existingIndex !== -1) {
          // Update existing entry with latest changes
          const updatedEditedTreatments = [...editedTreatments];
          updatedEditedTreatments[existingIndex] = updatedTreatment;
          setEditedTreatments(updatedEditedTreatments);
        } else {
          // Add new entry with current changes
          // console.log(updatedTreatment)
          setEditedTreatments([...editedTreatments, updatedTreatment]);
        }
      }

      updatedTreatments[index] = updatedTreatment;

      if (field === "name") {
        validateTreatmentName(value as string, index);
      }
    }
  };

  const handleAddTreatment = () => {
    const nextNumber = getNextTreatmentNumber();
    const newTreatment = {
      id: `treatment_${nextNumber}`,
      name: `treatment_${nextNumber}`,
      description: "",
      configuration: {},
      color: getNextAvailableColor(),
      default: false,
      rolloutPercentage: 0,
      featureId: "",
      realmId: "",
      userId: "",
      contextValues: {
        existing: [],
        new: [],
        deleted: [],
      },
      contextId: "",
      mappedSegments: {
        existing: [],
        new: [],
        deleted: [],
      },
    };

    setNewTreatments([...newTreatments, newTreatment]);
  };

  const handleRemoveTreatment = (index: number, isNew = false) => {
    if (isNew) {
      setNewTreatments(newTreatments.filter((_, i) => i !== index));
    } else {
      const treatmentToDelete = editedTreatments[index];
      setEditedTreatments(editedTreatments.filter((_, i) => i !== index));
      setDeletedTreatments([
        ...deletedTreatments,
        {
          projectId: id as string,
          featureId: featureId as string,
          treatmentId: treatmentToDelete.id,
        },
      ]);
    }
  };

  const handleSaveTreatment = async () => {
    try {
      // Create array of promises for parallel execution
      const createPromises = newTreatments.map((treatment) => {
        const payload = {
          treatmentName: treatment.name,
          rolloutPercentage: 0,
          configuration: treatment.configuration || {},
          treatmentdescription: treatment.description || null,
          projectId: String(id),
          featureId: String(featureId),
          default: false,
        };

        return createTreatmentsAsync(payload);
      });

      const deletePromises = deletedTreatments.map((treatment) => {
        return deleteTreatmentsAsync(treatment);
      });

      //Edit Treatment is pending

      const editedTreatmentsDiff = editedTreatments
        .map((editedTreatment) => {
          const existingTreatment = existingTreatments.find(
            (t) => t.id === editedTreatment.id
          );
          if (!existingTreatment) return null;

          const diff: Record<string, any> = {};

          (
            Object.keys(editedTreatment) as Array<keyof typeof editedTreatment>
          ).forEach((key) => {
            if (
              JSON.stringify(editedTreatment[key]) !==
              JSON.stringify(existingTreatment[key])
            ) {
              diff[key] = editedTreatment[key];
            }
          });

          return Object.keys(diff).length
            ? {
                ...editedTreatment,
                ...diff,
              }
            : null;
        })
        .filter(Boolean);

      const editPromises = editedTreatmentsDiff?.map((treatment) => {
        const payload = {
          projectId: id as string,
          featureId: featureId as string,
          treatmentId: treatment?.id || "",
          ...treatment,
        };
        return editTreatment(payload);
      });

      // Execute all promises in parallel
      await Promise.all([
        ...createPromises,
        ...deletePromises,
        ...editPromises,
      ]);
      // Reset states after successful save
      setDeletedTreatments([]);
      setNewTreatments([]);
      getAllTreatments();
    } catch (error) {
      console.error("Error creating treatments:", error);
    }
  };

  return {
    handleAddTreatment,
    handleRemoveTreatment,
    handleTreatmentChange,
    handleSaveTreatment,
  };
};
